/**
 * Represents the current state of a system artifact in the Fleet management workflow.
 */
export enum SystemArtifactState {
  /** Artifact collection has been requested but not yet started. Used for device-initiated artifact collection workflows. */
  Requested = 0,
  /** Artifact request has been received by the system who's retrieving it Used for device-initiated artifact collection workflows. */
  RequestReceived = 1,
  /** Artifact is currently being uploaded to cloud storage. */
  Uploading = 2,
  /** Artifact has been successfully uploaded and is available for download. */
  Uploaded = 3,
  /** Artifact collection or upload has failed. */
  Failed = 4,
  /** Artifact has been deleted. The blob has been removed but the record is retained for audit. */
  Deleted = 5,
  /** Artifact was automatically deleted by the retention policy. The blob has been removed but the record is retained for audit. */
  AutoDeleted = 6
}

export interface ArtifactStatusNotification {
  /** The system ID associated with this artifact. */
  systemId: string;
  /** The artifact ID. */
  artifactId: string;
  /** Status message describing the artifact collection result. */
  message: string;
  /** The current state of the artifact. */
  state: SystemArtifactState;
}

/**
 * Represents the status of a system data refresh saga.
 * Values match the C# RefreshSystemDataSaga.SystemDataRefreshStatus enum.
 */
export enum SystemDataRefreshStatus {
  /** IoT command has been sent to the system agent. */
  CommandSent = 'CommandSent',
  /** System agent has acknowledged receipt of the refresh request. */
  RequestReceived = 'RequestReceived',
  /** Data refresh has completed successfully (may be partial). */
  Completed = 'Completed',
  /** Data refresh has failed. */
  Failed = 'Failed',
  /** Data refresh has timed out waiting for agent response. */
  TimedOut = 'TimedOut'
}

/**
 * SignalR notification payload for system data refresh status updates.
 */
export interface SystemDataRefreshUpdate {
  /** Current status of the refresh operation. */
  status: SystemDataRefreshStatus;
  /** Optional message providing additional context. */
  message?: string;
  /** Whether environment data was successfully collected. */
  environmentSuccess?: boolean;
  /** Whether application data was successfully collected. */
  applicationsSuccess?: boolean;
  /** Whether licensing data was successfully collected. */
  licensingSuccess?: boolean;
  /** Timestamp when the last system data refresh was attempted. */
  dataRefreshedAt?: string;
}